import {
  AppBar,
  IconButton,
  makeStyles,
  Toolbar,
  Typography,
  Collapse,
  Tooltip,
  useMediaQuery,
} from '@material-ui/core';
import React,
{
  useState,
} from 'react';
import MenuIcon from '@material-ui/icons/Menu';
import {
  ArrowDropDown,
  ArrowDropUp,
} from '@material-ui/icons';
import {
  useTheme,
  Theme,
  createStyles,
} from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import CssBaseline from '@material-ui/core/CssBaseline';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';

import Hidden from '@material-ui/core/Hidden';
import {
  Switch,
  useHistory,
  useLocation,
} from 'react-router';
import {
  BrowserRouter,
  Route as BrowserRoute,
} from 'react-router-dom';
import {
  useLocalization,
  useStores,
} from 'hooks';
import {
  LANGUAGES,
  Language,
} from 'shared';
import {
  observer,
} from 'mobx-react';
import {
  Assets,
} from 'assets';
import Spinner from 'src/components/Spinner';
import i18n from 'i18n-js';
import {
  HeaderMenu,
  HeaderLanguages,
} from 'components';
import {
  ModuleTypes,
  Route,
  SideNavItem,
} from './types';
import {
  Module,
} from './modules';
import {
  RootRoute,
} from './routes';
import AuthRoutes from './authNavigator';
import {
  User,
} from '../stores/backend/auth/types';

const getSideNavItem = (rootRoute: Route<ModuleTypes>, items: SideNavItem, currentUser: User): SideNavItem => {
  if (rootRoute.config.parent && !rootRoute.config.sideNavConfig) {
    return items;
  }
  const sideNavChildren = rootRoute.children.filter(
    (child) => child.config.sideNavConfig && child.config.allowedRoles.includes(currentUser.role),
  );
  const navItem: SideNavItem = {
    name: i18n.t(rootRoute.config.name),
    icon: rootRoute.config.sideNavConfig?.icon,
    path: rootRoute.getPath(),
    children: sideNavChildren.length && rootRoute.config.type === ModuleTypes.CONTAINER
      ? sideNavChildren.map((child) => getSideNavItem(child, items, currentUser))
      : undefined,
  };
  return navItem;
};

const flattenRoutes = (rootRoute: Route<ModuleTypes>, routes: Route<ModuleTypes>[]): Route<ModuleTypes>[] => {
  let newRoutes = [...routes, rootRoute];
  if (!rootRoute.children.length) {
    return newRoutes;
  }
  rootRoute.children.forEach((route) => {
    newRoutes = flattenRoutes(route, newRoutes);
  });
  return newRoutes;
};

// eslint-disable-next-line max-len
const getSideNavItems = (rootRoute: Route<ModuleTypes>, items: SideNavItem, currentUser: User): SideNavItem[] => getSideNavItem(rootRoute, items, currentUser).children as any;

export const drawerWidth = 270;

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    display: 'flex',
    width: '100%',
    minHeight: '100vh',
    boxSizing: 'border-box',
    transition: theme.transitions.create(['padding', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  logo: {
    height: 80,
    width: 80,
  },
  appBar: {
    width: '100%',
    zIndex: theme.zIndex.drawer + 1,
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  menuButtonRtl: {
    marginLeft: theme.spacing(2),
  },
  // necessary for content to be below app bar
  toolbar: theme.mixins.toolbar,
  drawerPaper: {
    width: drawerWidth,
    overflowX: 'hidden',
    marginTop: 65,
    boxSizing: 'border-box',
    borderRight: '1px solid rgba(0, 0, 0, 0.12)',
  },
  content: {
    flexGrow: 1,
    backgroundColor: 'transparent',
    width: '100%',
    minHeight: '100vh',
    boxSizing: 'border-box',
    transition: theme.transitions.create(['margin', 'padding'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    flex: 1,
    alignItems: 'center',
  },
  search: {
    height: 30,
    width: 300,
    background: '#DDD',
    border: 'none',
  },
  spinnerContainer: {
    flexDirection: 'column',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    width: '100vw',
    height: '100vh',
  },
}));

interface Props {
  window?: () => Window;
}

export interface DrawerItemProps {
  item: SideNavItem;
  level: number;
  onItemClick?: () => void;
  isRTL?: boolean;
}

export const DrawerItem: React.FC<DrawerItemProps> = (props) => {
  const {
    item,
    level,
    onItemClick,
    isRTL,
  } = props;
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();

  const isChildActive = (navItem: SideNavItem): boolean => {
    if (navItem.path && location.pathname === navItem.path) return true;
    if (navItem.children && navItem.children.length) {
      return navItem.children.some(isChildActive);
    }
    return false;
  };

  const hasActiveChild = isChildActive(item);
  const [collapsed, setCollapsed] = useState(!hasActiveChild);

  React.useEffect(() => {
    if (hasActiveChild) {
      setCollapsed(false);
    }
  }, [location.pathname, hasActiveChild]);

  if (item.children && item.children.length) {
    return (
      <div>
        <ListItem
          button
          onClick={() => setCollapsed(!collapsed)}
          style={{
            paddingLeft: isRTL ? theme.spacing(2) : theme.spacing(2 + level * 2),
            paddingRight: isRTL ? theme.spacing(2 + level * 2) : theme.spacing(2),
            cursor: 'pointer',
          }}
        >
          {item.icon && (
            <ListItemIcon
              style={{
                minWidth: 40,
              }}
            >
              <img
                style={{
                  objectFit: 'contain',
                  width: 24,
                  height: 24,
                }}
                src={item.icon}
                alt={item.name}
              />
            </ListItemIcon>
          )}
          <ListItemText
            primary={item.name}
            primaryTypographyProps={{
              style: {
                fontWeight: level === 0 ? 600 : 500,
                fontSize: level === 0 ? 15 : 14,
                color: hasActiveChild ? theme.palette.primary.main : 'inherit',
              },
            }}
          />
          <IconButton
            size="small"
            edge="end"
            onClick={(e) => {
              e.stopPropagation();
              setCollapsed(!collapsed);
            }}
          >
            {collapsed ? <ArrowDropDown /> : <ArrowDropUp />}
          </IconButton>
        </ListItem>
        <Collapse in={!collapsed} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children.map((child) => (
              <DrawerItem
                key={child.path || child.name}
                item={child}
                level={level + 1}
                onItemClick={onItemClick}
                isRTL={isRTL}
              />
            ))}
          </List>
        </Collapse>
      </div>
    );
  }

  const isSelected = location.pathname === item.path;
  let itemFontWeight = 400;
  if (isSelected) {
    itemFontWeight = 600;
  } else if (level === 0) {
    itemFontWeight = 500;
  }

  return (
    <ListItem
      button
      key={item.path}
      selected={isSelected}
      onClick={() => {
        history.push(item.path);
        if (onItemClick) {
          onItemClick();
        }
      }}
      style={{
        paddingLeft: isRTL ? theme.spacing(2) : theme.spacing(2 + level * 2),
        paddingRight: isRTL ? theme.spacing(2 + level * 2) : theme.spacing(2),
        backgroundColor: isSelected ? 'rgba(63, 81, 181, 0.08)' : undefined,
        borderLeft: isSelected && !isRTL ? `4px solid ${theme.palette.primary.main || '#3f51b5'}` : undefined,
        borderRight: isSelected && isRTL ? `4px solid ${theme.palette.primary.main || '#3f51b5'}` : undefined,
      }}
    >
      {item.icon && (
        <ListItemIcon
          style={{
            minWidth: 40,
          }}
        >
          <img
            style={{
              objectFit: 'contain',
              width: 24,
              height: 24,
            }}
            src={item.icon}
            alt={item.name}
          />
        </ListItemIcon>
      )}
      <ListItemText
        primary={item.name}
        primaryTypographyProps={{
          style: {
            fontSize: level === 0 ? 15 : 14,
            fontWeight: itemFontWeight,
            color: isSelected ? theme.palette.primary.main || '#3f51b5' : 'inherit',
          },
        }}
      />
    </ListItem>
  );
};

const navigationContainer: React.FC<Props> = (props) => {
  const {
    window,
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('kashf_sidebar_open');
      return saved !== null ? saved === 'true' : true;
    } catch {
      return true;
    }
  });

  const stores = useStores();
  const {
    currentLanguage,
  } = stores.ui.localization;
  const {
    isRTL,
  } = currentLanguage;
  const [
    isLoading,
    setLoading,
  ] = useState(true);
  const {
    staticTexts,
  } = stores.backend.staticTexts;
  const {
    updateTranslations,
  } = useLocalization();

  const initLocalization = async () => {
    let language: Language = stores.ui.localization.currentLanguage;
    if (!language) {
      const deviceLocale = (window ? window().navigator.language : 'en-US').split('-')[0];
      const languageIndex = deviceLocale === 'ar' ? 1 : 0;
      language = LANGUAGES[languageIndex];
    }
    if (staticTexts.data && staticTexts.data.length) {
      updateTranslations(language);
    } else {
      await updateTranslations(language);
    }
  };

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      await stores.hydrate();
      await initLocalization();
      if (stores.backend.auth.isLoggedIn) {
        await stores.backend.auth.updateTokenHeader(stores.backend.auth.getAccessToken());
        await stores.backend.auth.getUser();
      }
      document.body.setAttribute('dir', stores.ui.localization.currentLanguage.isRTL ? 'rtl' : 'ltr');
      setLoading(false);
    })();
  }, []);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDesktopOpen((prev) => {
        const next = !prev;
        try {
          localStorage.setItem('kashf_sidebar_open', String(next));
        } catch {
          // ignore
        }
        return next;
      });
    }
  };

  const handleMobileItemClick = () => {
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <div>
      <Divider />
      {stores.backend.auth.isLoggedIn && stores.backend.auth.currentUser
        ? (
          <List>
            {getSideNavItems(
              RootRoute,
              {} as any,
              stores.backend.auth.currentUser,
            )?.map((item) => (
              <DrawerItem
                key={item.path || item.name}
                item={item}
                level={0}
                onItemClick={handleMobileItemClick}
                isRTL={isRTL}
              />
            ))}
          </List>
        ) : ''}
    </div>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  if (isLoading) {
    return (
      <div
        className={classes.spinnerContainer}
      >
        <Spinner />
      </div>
    );
  }

  let rootPaddingLeft = 0;
  let rootPaddingRight = 0;
  if (!isMobile && desktopOpen) {
    if (isRTL) {
      rootPaddingRight = drawerWidth;
    } else {
      rootPaddingLeft = drawerWidth;
    }
  }

  return (
    <BrowserRouter>
      {stores.backend.auth.isLoggedIn && stores.backend.auth.currentUser
        ? (
          <div
            className={classes.root}
            style={{
              paddingLeft: rootPaddingLeft,
              paddingRight: rootPaddingRight,
              paddingTop: 24,
            }}
          >
            <CssBaseline />
            <AppBar position="fixed" className={classes.appBar}>
              <Toolbar>
                <Tooltip title={desktopOpen ? 'Fold sidebar' : 'Unfold sidebar'}>
                  <IconButton
                    color="inherit"
                    aria-label="toggle drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    className={isRTL ? classes.menuButtonRtl : classes.menuButton}
                  >
                    <MenuIcon />
                  </IconButton>
                </Tooltip>
                <div className={classes.header}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <img
                      src={Assets.images.common.logo}
                      alt="Kashf"
                      style={{
                        height: 50,
                      }}
                    />
                    <Typography
                      variant="h6"
                      noWrap
                      style={{
                        lineHeight: '50px',
                        marginLeft: isRTL ? 0 : 16,
                        marginRight: isRTL ? 16 : 0,
                      }}
                    >
                      Kashf
                    </Typography>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                    }}
                  >
                    <HeaderLanguages />
                    <HeaderMenu />
                  </div>
                </div>
              </Toolbar>
            </AppBar>
            <nav>
              <Hidden smUp implementation="css">
                <Drawer
                  container={container}
                  variant="temporary"
                  anchor={isRTL ? 'right' : 'left'}
                  open={mobileOpen}
                  onClose={handleDrawerToggle}
                  classes={{
                    paper: classes.drawerPaper,
                  }}
                  ModalProps={{
                    keepMounted: true, // Better open performance on mobile.
                  }}
                >
                  {drawer}
                </Drawer>
              </Hidden>
              <Hidden xsDown implementation="css">
                <Drawer
                  classes={{
                    paper: classes.drawerPaper,
                  }}
                  variant="persistent"
                  anchor={isRTL ? 'right' : 'left'}
                  open={desktopOpen}
                >
                  {drawer}
                </Drawer>
              </Hidden>
            </nav>
            <main className={classes.content}>
              <div
                className={classes.toolbar}
              />
              <Switch>
                {flattenRoutes(RootRoute, []).map((route) => (
                  <BrowserRoute
                    key={route.getPath()}
                    path={route.getPath()}
                    exact
                    component={() => <Module route={route} />}
                  />
                ))}
              </Switch>
            </main>
          </div>
        ) : (
          <AuthRoutes />
        )}
    </BrowserRouter>
  );
};
export const NavigationContainer = observer(navigationContainer);
